import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@repo/ui";
import { PlusIcon, RefreshCwIcon, SaveIcon, TrashIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import {
  type DailyReportInstitutionItem,
  type DailyReportItem,
  useCreateDailyReport,
  useDailyReports,
  useDeleteDailyReport,
  useUpdateDailyReport,
} from "@/lib/queries/use-daily-reports";
import AdminNav from "@/pages/admin/admin-nav";

interface InstitutionInput {
  institution_name: string;
  loan_units: string;
  loan_sales: string;
  npa_units: string;
  npa_sales: string;
}

export default function DailyReportsPage() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [visitorsCount, setVisitorsCount] = useState("");
  const [stickersCount, setStickersCount] = useState("");
  const [institutions, setInstitutions] = useState<InstitutionInput[]>([]);

  const { data, isLoading, refetch, isFetching } = useDailyReports();
  const createMutation = useCreateDailyReport();
  const updateMutation = useUpdateDailyReport(editingId ?? 0);
  const deleteMutation = useDeleteDailyReport();

  useEffect(() => {
    document.title = "รายงานประจำวัน | House & Condo Expo";
  }, []);

  const reports: DailyReportItem[] = data?.reports ?? [];

  const addInstitution = () => {
    setInstitutions([
      ...institutions,
      {
        institution_name: "",
        loan_units: "",
        loan_sales: "",
        npa_units: "",
        npa_sales: "",
      },
    ]);
  };

  const removeInstitution = (index: number) => {
    setInstitutions(institutions.filter((_, i) => i !== index));
  };

  const updateInstitution = (index: number, field: keyof InstitutionInput, value: string) => {
    const updated = [...institutions];
    updated[index][field] = value;
    setInstitutions(updated);
  };

  const resetForm = () => {
    setEditingId(null);
    const today = new Date();
    setDate(today.toISOString().split("T")[0]);
    setVisitorsCount("");
    setStickersCount("");
    setInstitutions([]);
  };

  const loadReportForEdit = (reportId: number) => {
    const report = reports.find((r) => r.id === reportId);
    if (!report) return;

    setEditingId(reportId);
    setDate(report.date.split("T")[0]);
    setVisitorsCount(report.visitors_count?.toString() ?? "");
    setStickersCount(report.stickers_count?.toString() ?? "");

    // Fetch full report details to get institutions
    fetch(`/api/admin/daily-reports/${reportId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      },
    })
      .then(
        (res) => res.json() as Promise<{ report: { institutions: DailyReportInstitutionItem[] } }>
      )
      .then((data) => {
        const reportData = data.report;
        setInstitutions(
          reportData.institutions.map((inst) => ({
            institution_name: inst.institution_name,
            loan_units: inst.loan_units?.toString() ?? "",
            loan_sales: inst.loan_sales ?? "",
            npa_units: inst.npa_units?.toString() ?? "",
            npa_sales: inst.npa_sales ?? "",
          }))
        );
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "ไม่สามารถโหลดข้อมูลรายงานได้",
          variant: "destructive",
        });
      });
  };

  const handleSave = async () => {
    if (!date) {
      toast({
        title: "Error",
        description: "กรุณาเลือกวันที่",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      date,
      visitors_count: visitorsCount ? parseInt(visitorsCount, 10) : null,
      stickers_count: stickersCount ? parseInt(stickersCount, 10) : null,
      institutions: institutions
        .filter((inst) => inst.institution_name.trim() !== "")
        .map((inst) => ({
          institution_name: inst.institution_name.trim(),
          loan_units: inst.loan_units ? parseInt(inst.loan_units, 10) : null,
          loan_sales: inst.loan_sales ? parseFloat(inst.loan_sales) : null,
          npa_units: inst.npa_units ? parseInt(inst.npa_units, 10) : null,
          npa_sales: inst.npa_sales ? parseFloat(inst.npa_sales) : null,
        })),
    };

    try {
      if (editingId) {
        await updateMutation.mutateAsync(payload);
        toast({
          title: "สำเร็จ",
          description: "อัปเดตรายงานเรียบร้อยแล้ว",
        });
      } else {
        await createMutation.mutateAsync(payload);
        toast({
          title: "สำเร็จ",
          description: "สร้างรายงานเรียบร้อยแล้ว",
        });
      }
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "เกิดข้อผิดพลาด",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการลบรายงานนี้?")) return;

    try {
      await deleteMutation.mutateAsync(id);
      toast({
        title: "สำเร็จ",
        description: "ลบรายงานเรียบร้อยแล้ว",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "เกิดข้อผิดพลาด",
        variant: "destructive",
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold">รายงานประจำวัน</h1>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCwIcon className={isFetching ? "animate-spin" : ""} />
            รีเฟรช
          </Button>
        </div>

        {/* Form Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{editingId ? "แก้ไขรายงาน" : "สร้างรายงานใหม่"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date and basic counts */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="date">วันที่</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={editingId !== null}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="visitors">จำนวนผู้เข้าชม</Label>
                <Input
                  id="visitors"
                  type="number"
                  placeholder="ไม่บังคับ"
                  value={visitorsCount}
                  onChange={(e) => setVisitorsCount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stickers">จำนวนสติกเกอร์</Label>
                <Input
                  id="stickers"
                  type="number"
                  placeholder="ไม่บังคับ"
                  value={stickersCount}
                  onChange={(e) => setStickersCount(e.target.value)}
                />
              </div>
            </div>

            {/* Institutions Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">ข้อมูลสินเชื่อและ NPA ตามสถาบัน</Label>
                <Button type="button" variant="outline" size="sm" onClick={addInstitution}>
                  <PlusIcon />
                  เพิ่มสถาบัน
                </Button>
              </div>

              {institutions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  ยังไม่มีข้อมูลสถาบัน คลิก &quot;เพิ่มสถาบัน&quot; เพื่อเริ่มต้น
                </p>
              ) : (
                <div className="space-y-4">
                  {institutions.map((inst, index) => (
                    <Card key={index} className="border-muted">
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">สถาบัน #{index + 1}</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeInstitution(index)}
                            >
                              <XIcon />
                            </Button>
                          </div>
                          <div className="grid gap-4 md:grid-cols-5">
                            <div className="space-y-2">
                              <Label htmlFor={`inst-name-${index}`}>ชื่อสถาบัน</Label>
                              <Input
                                id={`inst-name-${index}`}
                                value={inst.institution_name}
                                onChange={(e) =>
                                  updateInstitution(index, "institution_name", e.target.value)
                                }
                                placeholder="เช่น ธนาคาร ABC"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`loan-units-${index}`}>สินเชื่อ (ยูนิต)</Label>
                              <Input
                                id={`loan-units-${index}`}
                                type="number"
                                value={inst.loan_units}
                                onChange={(e) =>
                                  updateInstitution(index, "loan_units", e.target.value)
                                }
                                placeholder="0"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`loan-sales-${index}`}>สินเชื่อ (ยอดขาย)</Label>
                              <Input
                                id={`loan-sales-${index}`}
                                type="number"
                                step="0.01"
                                value={inst.loan_sales}
                                onChange={(e) =>
                                  updateInstitution(index, "loan_sales", e.target.value)
                                }
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`npa-units-${index}`}>NPA (ยูนิต)</Label>
                              <Input
                                id={`npa-units-${index}`}
                                type="number"
                                value={inst.npa_units}
                                onChange={(e) =>
                                  updateInstitution(index, "npa_units", e.target.value)
                                }
                                placeholder="0"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`npa-sales-${index}`}>NPA (ยอดขาย)</Label>
                              <Input
                                id={`npa-sales-${index}`}
                                type="number"
                                step="0.01"
                                value={inst.npa_sales}
                                onChange={(e) =>
                                  updateInstitution(index, "npa_sales", e.target.value)
                                }
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isSaving}>
                <SaveIcon />
                {isSaving ? "กำลังบันทึก..." : editingId ? "อัปเดต" : "บันทึก"}
              </Button>
              {editingId && (
                <Button variant="outline" onClick={resetForm}>
                  ยกเลิก
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        <Card>
          <CardHeader>
            <CardTitle>รายงานที่บันทึกไว้</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-center text-muted-foreground">กำลังโหลด...</p>
            ) : reports.length === 0 ? (
              <p className="text-center text-muted-foreground">ยังไม่มีรายงาน</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>วันที่</TableHead>
                      <TableHead>งาน</TableHead>
                      <TableHead className="text-right">ผู้เข้าชม</TableHead>
                      <TableHead className="text-right">สติกเกอร์</TableHead>
                      <TableHead className="text-right">อัปเดตล่าสุด</TableHead>
                      <TableHead className="text-right">การดำเนินการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          {new Date(report.date).toLocaleDateString("th-TH", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </TableCell>
                        <TableCell>งาน #{report.expo_version}</TableCell>
                        <TableCell className="text-right">{report.visitors_count ?? "-"}</TableCell>
                        <TableCell className="text-right">{report.stickers_count ?? "-"}</TableCell>
                        <TableCell className="text-right">
                          {new Date(report.updated_at).toLocaleDateString("th-TH", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadReportForEdit(report.id)}
                            >
                              แก้ไข
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(report.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <TrashIcon />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
